import { useState, useEffect } from 'react';
import { useOrg } from '../contexts/OrgContext';
import { useAuth } from '../contexts/AuthContext';
import { useSync } from '../contexts/SyncContext';
import {
  getAvailableInspections,
  getReservedInspections,
  reserveInspection,
  unreserveInspection,
  Inspection,
} from '../services/inspectionService';
import {
  CheckCircleIcon,
  ClockIcon,
  MapPinIcon,
  CubeIcon,
  ArrowPathIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

export function MyJobsPage() {
  const { currentOrg } = useOrg();
  const { currentUser } = useAuth();
  const { syncData, isSyncing } = useSync();

  const [availableJobs, setAvailableJobs] = useState<Inspection[]>([]);
  const [reservedJobs, setReservedJobs] = useState<Inspection[]>([]);
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    loadJobs();
  }, [currentOrg, currentUser]);

  const loadJobs = async () => {
    if (!currentOrg || !currentUser) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [available, reserved] = await Promise.all([
        getAvailableInspections(currentOrg.id),
        getReservedInspections(currentOrg.id, currentUser.uid),
      ]);

      setAvailableJobs(available);
      setReservedJobs(reserved);
    } catch (error) {
      console.error('Failed to load jobs:', error);
      alert('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const toggleJobSelection = (jobId: string) => {
    const newSelection = new Set(selectedJobs);
    if (newSelection.has(jobId)) {
      newSelection.delete(jobId);
    } else {
      newSelection.add(jobId);
    }
    setSelectedJobs(newSelection);
  };

  const selectAll = () => {
    const allJobIds = new Set(availableJobs.map(j => j.id));
    setSelectedJobs(allJobIds);
  };

  const deselectAll = () => {
    setSelectedJobs(new Set());
  };

  const handleReserveAndSync = async () => {
    if (!currentOrg || !currentUser || selectedJobs.size === 0) return;

    try {
      setSyncing(true);

      // Reserve all selected jobs
      for (const jobId of selectedJobs) {
        await reserveInspection(
          currentOrg.id,
          jobId,
          currentUser.uid,
          currentUser.displayName || currentUser.email || 'User'
        );
      }

      // Sync all data
      await syncData();

      // Reload jobs
      await loadJobs();

      // Clear selection
      setSelectedJobs(new Set());

      alert(`Successfully reserved and synced ${selectedJobs.size} job(s)`);
    } catch (error) {
      console.error('Failed to reserve and sync:', error);
      alert('Failed to reserve and sync jobs');
    } finally {
      setSyncing(false);
    }
  };

  const handleUnreserve = async (jobId: string) => {
    if (!currentOrg) return;
    if (!confirm('Are you sure you want to unreserve this job?')) return;

    try {
      await unreserveInspection(currentOrg.id, jobId);
      await loadJobs();
    } catch (error) {
      console.error('Failed to unreserve job:', error);
      alert('Failed to unreserve job');
    }
  };

  const handleSyncReserved = async () => {
    if (!currentOrg) return;

    try {
      setSyncing(true);
      await syncData();
      alert('Successfully synced all reserved jobs');
    } catch (error) {
      console.error('Failed to sync:', error);
      alert('Failed to sync data');
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (date: any) => {
    if (!date) return 'Not scheduled';
    if (typeof date === 'number') return new Date(date).toLocaleDateString();
    if (date.toDate) return date.toDate().toLocaleDateString();
    if (date.seconds) return new Date(date.seconds * 1000).toLocaleDateString();
    return new Date(date).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading jobs...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">My Jobs</h1>
        <p className="text-gray-600">
          Select jobs to reserve for offline work and sync the required data
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Available Jobs */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Available Jobs ({availableJobs.length})
            </h2>
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="text-sm text-primary-600 hover:text-primary-700"
                disabled={availableJobs.length === 0}
              >
                Select All
              </button>
              <button
                onClick={deselectAll}
                className="text-sm text-gray-600 hover:text-gray-700"
                disabled={selectedJobs.size === 0}
              >
                Deselect All
              </button>
            </div>
          </div>

          {availableJobs.length === 0 ? (
            <div className="text-center py-8">
              <ClockIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-sm text-gray-600">No available jobs</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {availableJobs.map((job) => (
                <div
                  key={job.id}
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                    selectedJobs.has(job.id)
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => toggleJobSelection(job.id)}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedJobs.has(job.id)}
                      onChange={() => toggleJobSelection(job.id)}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 mb-1">
                        {job.templateName}
                      </p>
                      <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                        {job.flocName && (
                          <div className="flex items-center gap-1">
                            <MapPinIcon className="w-4 h-4" />
                            <span>{job.flocName}</span>
                          </div>
                        )}
                        {job.assetName && (
                          <div className="flex items-center gap-1">
                            <CubeIcon className="w-4 h-4" />
                            <span>{job.assetName}</span>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(job.scheduledDate)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {selectedJobs.size > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <button
                onClick={handleReserveAndSync}
                disabled={syncing || isSyncing}
                className="btn btn-primary w-full flex items-center justify-center"
              >
                {syncing || isSyncing ? (
                  <>
                    <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
                    Reserving & Syncing...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="w-5 h-5 mr-2" />
                    Reserve & Sync Selected ({selectedJobs.size})
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Reserved Jobs */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              My Reserved Jobs ({reservedJobs.length})
            </h2>
            {reservedJobs.length > 0 && (
              <button
                onClick={handleSyncReserved}
                disabled={syncing || isSyncing}
                className="btn btn-secondary btn-sm flex items-center"
              >
                <ArrowPathIcon className={`w-4 h-4 mr-1 ${(syncing || isSyncing) ? 'animate-spin' : ''}`} />
                Sync All
              </button>
            )}
          </div>

          {reservedJobs.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircleIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-sm text-gray-600">No reserved jobs</p>
              <p className="text-xs text-gray-500 mt-1">
                Select jobs from available list to reserve
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {reservedJobs.map((job) => (
                <div
                  key={job.id}
                  className="p-3 border-2 border-green-200 bg-green-50 rounded-lg"
                >
                  <div className="flex items-start gap-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 mb-1">
                        {job.templateName}
                      </p>
                      <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                        {job.flocName && (
                          <div className="flex items-center gap-1">
                            <MapPinIcon className="w-4 h-4" />
                            <span>{job.flocName}</span>
                          </div>
                        )}
                        {job.assetName && (
                          <div className="flex items-center gap-1">
                            <CubeIcon className="w-4 h-4" />
                            <span>{job.assetName}</span>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(job.scheduledDate)}
                      </p>
                    </div>
                    <button
                      onClick={() => handleUnreserve(job.id)}
                      className="text-red-600 hover:text-red-700"
                      title="Unreserve job"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-6 card bg-blue-50 border border-blue-200">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">
          How Job Reservation Works
        </h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Select jobs from the available list that you plan to complete</li>
          <li>• Click "Reserve & Sync" to claim jobs and download all required data</li>
          <li>• Reserved jobs are visible to other users (prevents duplicate work)</li>
          <li>• All templates, FLOCs, assets, and documents are synced for offline access</li>
          <li>• You can work completely offline once data is synced</li>
          <li>• Unreserve jobs if your plans change</li>
        </ul>
      </div>
    </div>
  );
}
